import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useParams, useRouteMatch } from 'react-router-dom';
import { DATE_FORMAT_URL, DATE_ISO_FORMAT } from '../../../../data/constants/datetime';
import { useEvent } from '../../../../data/hooks/eventHooks';
import { useI18n } from '../../../../data/hooks/i18nHooks';
import { useMeeting } from '../../../../data/hooks/meeting/useMeeting';
import { useProgramSession } from '../../../../data/hooks/schedule/useProgramSession';
import { eventMomentFactory } from '../../../../data/misc/eventMomentFactory';
import Scrollable from '../../../elements/Scrollable';
import TopBarEvent from '../../../elements/TopBar/TopBarEvent';
import LoadLocations from '../../../loaders/LoadLocations';
import LoadMeetings from '../../../loaders/LoadMeetings';
import LoadParticipants from '../../../loaders/LoadParticipants';
import LoadSpeakers from '../../../loaders/LoadSpeakers';
import WaitWhileBusy from '../../../WaitWhileBusy';
import Screen from '../../Screen';
import { useSlotSelection } from './useSlotSelection';
import DateSelection from './DateSelection';
import { InviteParticipants, useInvitedParticipantsState } from './invitees/InviteParticipants';
import { InviteSpeakers, useInvitedSpeakersState } from './invitees/InviteSpeakers/index';
import LocationSelection from './LocationSelection';
import SlotSelection from './SlotSelection';
import MotiveInput from './MotiveInput';
import CreateMeetingButton from './CreateMeetingButton/CreateMeetingButton';
import styles from './InviteToMeetingScreen.module.scss';

const MEETING_STEP_WHO = 0,
    MEETING_STEP_WHEN_DATE = 1,
    MEETING_STEP_WHEN_TIME = 2,
    MEETING_STEP_WHERE = 3,
    MEETING_STEP_WHY = 4;

function InviteToMeetingScreen(props) {
    const { eventId, itemId, returnURL = '..' } = props,
        event = useEvent(eventId),
        i18n = useI18n(),
        programItem = useProgramSession(eventId, itemId),
        { date: urlDate, meetingId: rescheduleMeetingId } = useParams(),
        isReschedule = useRouteMatch().path.match(/reschedule/),
        rescheduleMeeting = useMeeting(eventId, isReschedule ? rescheduleMeetingId : null),
        [step, setStep] = useState(MEETING_STEP_WHO),
        [participants, setParticipants] = useInvitedParticipantsState(eventId, { rescheduleMeeting }),
        [speakers, setSpeakers] = useInvitedSpeakersState(eventId),
        [slot, setSlot, availableSlots, updatingSlots] = useSlotSelection(eventId, participants, speakers, programItem?.display_type == 'speeddate' ? programItem : null),
        [date, setDate] = useState(urlDate ? eventMomentFactory(event, urlDate, DATE_FORMAT_URL).format(DATE_ISO_FORMAT) : null),
        [location, setLocation] = useState(rescheduleMeeting?.location),
        [motiv, setMotiv] = useState('');

    useEffect(() => {
        if (updatingSlots) {
            app.store.dispatch(app.actions.ui.busy());
            return () => app.store.dispatch(app.actions.ui.free());
        }
    }, [updatingSlots]);

    const header = (
            <>
                <TopBarEvent event={event} returnURL={returnURL}>
                    {rescheduleMeeting ? i18n['label.reschedule-meeting'] : i18n['label.invite']}
                </TopBarEvent>
                <hr />
            </>
        ),
        footer =
            step >= MEETING_STEP_WHY ? (
                <div className="content">
                    <CreateMeetingButton event={event} participants={participants} speakers={speakers} slot={slot} location={location} motiv={motiv} rescheduleMeeting={rescheduleMeeting} />
                </div>
            ) : null,
        steps = [
            // Invitees
            <ul key={MEETING_STEP_WHO} className="list">
                <InviteParticipants eventId={eventId} selected={participants} onChange={setParticipants} />
                <InviteSpeakers eventId={eventId} selected={speakers} onChange={setSpeakers} />
            </ul>,
            <DateSelection key={MEETING_STEP_WHEN_DATE} event={event} slots={availableSlots} date={date} onChange={setDate} updatingSlots={updatingSlots} />,
            <SlotSelection key={MEETING_STEP_WHEN_TIME} event={event} date={date} slots={availableSlots} slot={slot} onChange={setSlot} />,
            <LocationSelection key={MEETING_STEP_WHERE} event={event} slot={slot} location={location} onChange={setLocation} />,
            <MotiveInput key={MEETING_STEP_WHY} slot={slot} motiv={motiv} onChange={setMotiv} />,
        ].filter((sc, i) => i <= step);

    // Get out if there's nobody to invite (just on the first run; after that the button will just be disabled)
    useEffect(() => {
        if (participants.length + speakers.length == 0) {
            console.warn('Nobody to be invited');
            app.history.push(returnURL);
        } else {
            setStep(MEETING_STEP_WHEN_DATE);
        }
    }, []);

    // Adjust step based on state
    useEffect(() => {
        //
        // go back if something is missing
        //
        if (!date && step > MEETING_STEP_WHEN_DATE) {
            setStep(MEETING_STEP_WHEN_DATE);
        } else if (!slot && step > MEETING_STEP_WHEN_TIME) {
            setStep(MEETING_STEP_WHEN_TIME);
        } else if (!location && step > MEETING_STEP_WHERE) {
            setStep(MEETING_STEP_WHERE);
        }
        //
        // advance when step is complete
        //
        else if (location && step <= MEETING_STEP_WHERE) {
            setStep(MEETING_STEP_WHY);
        } else if (slot && step <= MEETING_STEP_WHEN_TIME) {
            setStep(MEETING_STEP_WHERE);
        } else if (date && step <= MEETING_STEP_WHEN_DATE) {
            setStep(MEETING_STEP_WHEN_TIME);
        }
    }, [date, slot, location]);

    // useEffect(() => console.log('DBG STEP', step, steps), [step]);
    // useEffect(() => console.log('DBG SLOTS', availableSlots), [availableSlots]);

    return (
        <Screen header={header} footer={footer}>
            <Scrollable className={styles.InviteToMeetingScreen}>{steps}</Scrollable>
        </Screen>
    );
}

export default () => {
    const params = useParams(),
        { eventId } = params;

    return (
        <LoadParticipants eventId={eventId}>
            <LoadSpeakers eventId={eventId}>
                <LoadLocations eventId={eventId}>
                    <LoadMeetings eventId={eventId}>
                        <WaitWhileBusy>
                            <InviteToMeetingScreen {...params} key={location.search} />
                        </WaitWhileBusy>
                    </LoadMeetings>
                </LoadLocations>
            </LoadSpeakers>
        </LoadParticipants>
    );
};
